import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import InitializeTimerNodePreview from "../../features/sequence/node_previews/InitializeTimerNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export interface IInitializeTimer extends Interaction {
  startAuto: boolean;
  startTime: number;
  maxTime: number;
  speed: number;
  colorBackground: { r: number; g: number; b: number; a: number };
  colorFill: { r: number; g: number; b: number; a: number };
  colorCurrent: { r: number; g: number; b: number; a: number };
  onFinishedEvent: string;
  useAudio: boolean;
  urgentTime: number;
  urgentColor: { r: number; g: number; b: number; a: number };
}

export function getInitializeTimerNodePreview(timer: IInitializeTimer): string {
  return `Init Timer (${timer.maxTime}s)`;
}

export const initializeTimerInteraction = (): InteractionFactoryEntity => {
  const create = (
    getKeys: (amount: number) => string[]
  ): IInitializeTimer => {
    return {
      id: GetNewGuid(),
      name: "Initialize Timer",
      type: InteractionType.InitializeTimer,
      startAuto: false,
      startTime: 0,
      maxTime: 1,
      speed: 1,
      colorBackground: { r: 127, g: 255, b: 0, a: 1 },
      colorFill: { r: 86, g: 86, b: 86, a: 1 },
      colorCurrent: { r: 63, g: 127, b: 0, a: 1 },
      onFinishedEvent: "",
      useAudio: false,
      urgentTime: 0,
      urgentColor: { r: 204, g: 76, b: 76, a: 1 },
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IInitializeTimer => {
    return interaction as IInitializeTimer;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[]
  ): IInitializeTimer => {
    const casted = interaction as IInitializeTimer;
    return {
      ...casted,
      id: GetNewGuid(),
      name: "Initialize Timer",
      type: InteractionType.InitializeTimer,
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string
  ): string[] => {
    return [];
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[]
  ): string[] => {
    const casted = interaction as IInitializeTimer;
    const errors: string[] = [];

    if (!Number.isFinite(casted.maxTime) || casted.maxTime <= 0) {
      errors.push("Max Time must be greater than 0");
    }
    if (!Number.isFinite(casted.speed) || casted.speed < 0) {
      errors.push("Speed must be 0 or greater");
    }
    if (!Number.isFinite(casted.urgentTime) || casted.urgentTime < 0) {
      errors.push("Urgent Time must be 0 or greater");
    }

    return errors;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[]
  ): SearchResultData[] => {
    const casted = interaction as IInitializeTimer;
    return getMatchingResults(
      [interaction.name],
      takeName,
      interaction,
      "Initialize Timer"
    );
  };

  return {
    View: <></>,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <InitializeTimerNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: "Initialize Timer",
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
